import { useCallback, useMemo, useRef, useState } from 'react'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import { Input, message, Tabs, Typography } from 'antd'
import * as api from '../lib/api'
import { useI18n } from '../i18n/I18nProvider'

const { TextArea } = Input
const { Text } = Typography

const formats = ['bold', 'italic', 'underline', 'image']

function asEditorHtml (value) {
  const text = value || ''
  if (/<(?:p|br|strong|em|u|ol|ul|li|img)\b/i.test(text)) return text
  return text
    .split(/\n{2,}/)
    .map(block => `<p>${block.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('\n', '<br>')}</p>`)
    .join('')
}

export default function EmailTemplateEditor ({ sessionId, value, onChange, disabled }) {
  const { t } = useI18n()
  const editorRef = useRef(null)
  const [mode, setMode] = useState('visual')

  const chooseAndUploadImage = useCallback(() => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/png,image/jpeg,image/gif,image/webp'
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      try {
        const { url } = await api.uploadSessionEmailTemplateImage(sessionId, file)
        const editor = editorRef.current?.getEditor()
        const range = editor?.getSelection(true)
        if (editor && range) {
          editor.insertEmbed(range.index, 'image', url, 'user')
          editor.setSelection(range.index + 1, 0)
        }
      } catch (error) {
        message.error(error.message || t('session.emails.messages.image_upload_failed', 'Unable to upload image'))
      }
    }
    input.click()
  }, [sessionId, t])

  const modules = useMemo(() => ({
    toolbar: disabled
      ? false
      : {
          container: [
            ['bold', 'italic', 'underline'],
            ['image'],
            ['clean']
          ],
          handlers: { image: chooseAndUploadImage }
        }
  }), [chooseAndUploadImage, disabled])

  return (
    <div style={{ width: '100%' }}>
      <Tabs
        activeKey={mode}
        onChange={setMode}
        items={[
          { key: 'visual', label: t('session.emails.editor.visual', 'Visual editor') },
          { key: 'html', label: t('session.emails.editor.html', 'HTML') }
        ]}
        tabBarStyle={{ marginBottom: 12 }}
      />
      {mode === 'html'
        ? (
          <>
            <Text type='secondary' style={{ display: 'block', marginBottom: 8 }}>
              {t('session.emails.editor.html_safety_note', 'Full HTML documents and custom CSS are supported. Rendering may vary between email clients.')}
            </Text>
            <TextArea
              disabled={disabled}
              value={value || ''}
              onChange={event => onChange(event.target.value)}
              autoSize={{ minRows: 12 }}
              placeholder={t('session.emails.body_placeholder', 'Write the email body...')}
              style={{ width: '100%', fontFamily: 'monospace' }}
            />
          </>
          )
        : (
          <ReactQuill
            ref={editorRef}
            theme='snow'
            value={asEditorHtml(value)}
            onChange={onChange}
            readOnly={disabled}
            modules={modules}
            formats={formats}
            placeholder={t('session.emails.body_placeholder', 'Write the email body...')}
            style={{ width: '100%', background: disabled ? 'var(--neutral-grey0)' : '#fff' }}
          />
          )}
    </div>
  )
}
